/**
 * <CardIllustration /> — soft, geometric SVG illustrations for absorption cards.
 *
 * Design rules (matches user spec):
 *   - Flat / semi-flat geometric forms
 *   - Soft gradients (sky / pale-blue / sand backplates)
 *   - Rounded corners on shapes
 *   - Single hair-thin contour stroke (~2px)
 *   - Calm palette — no bright primaries, no childish saturation
 *   - Slight inner softness, never realistic
 *
 * Dispatch:
 *   - Pass `illustrationKey` for explicit control
 *   - OR omit, and the component derives a key from card text
 *   - Falls back to a generic abstract dawn glyph
 */
import { useMemo, useState } from "react";

const PALETTE = {
  sky:        "#DCEEF8",
  skySoft:    "#EAF4FB",
  paleSand:   "#F5F1E8",
  cream:      "#FAF7F2",
  green:      "#E7F3EC",
  greenInk:   "#7FA98B",
  lavender:   "#EFEAF6",
  gold:       "#C6A96B",
  amber:      "#E6C27A",
  ink:        "#3A4254",        // muted blue-grey, never harsh black
  inkSoft:    "#6A7184",
  rust:       "#C58866",
  red:        "#D4827A",        // soft, never alarming
};

// ─── Illustration keyword detection ────────────────────────────────
// Maps text fragments → illustration keys. First match wins. Order matters.
const KEY_MATCHERS = [
  ["solid white line",    "lineSolidWhite"],
  ["double white",         "lineDoubleWhite"],
  ["centre line",          "lineCentre"],
  ["dotted",               "lineDashed"],
  ["white line",           "lineSolidWhite"],

  ["roundabout",           "roundabout"],
  ["junction",             "junction"],
  ["crossroads",           "junction"],
  ["box junction",         "junction"],

  ["zebra crossing",       "pedestrianCrossing"],
  ["pelican",              "pedestrianCrossing"],
  ["pedestrian",           "pedestrianCrossing"],

  ["cyclist",              "cyclist"],
  ["bicycle",              "cyclist"],
  ["horse",                "horseRider"],

  ["motorway",             "motorway"],
  ["dual carriage",        "motorway"],

  ["speed",                "speedSign"],
  ["mph",                  "speedSign"],

  ["stop",                 "stopSign"],
  ["give way",             "giveWaySign"],

  ["traffic light",        "trafficLight"],
  ["amber",                "trafficLight"],

  ["triangle",             "warningTriangle"],
  ["warning",              "warningTriangle"],
  ["blue circular",        "circleMandatory"],
  ["blue background",      "circleMandatory"],
  ["mandatory",            "circleMandatory"],
  ["circular sign",        "circleSign"],
  ["circle",               "circleSign"],
  ["red border",           "circleSign"],
  ["rectangular",          "infoSign"],
  ["road sign",            "warningTriangle"],

  ["tyre",                 "tyre"],
  ["tire",                 "tyre"],
  ["pressure",             "tyre"],
  ["tread",                "tyre"],

  ["brake",                "brakes"],
  ["abs",                  "brakes"],

  ["headlight",            "headlights"],
  ["light",                "headlights"],
  ["indicator",            "headlights"],
  ["hazard",               "hazardLights"],

  ["fog",                  "fog"],
  ["rain",                 "rain"],
  ["wet",                  "rain"],
  ["ice",                  "ice"],
  ["snow",                 "ice"],
  ["sun",                  "sun"],
  ["dazzl",                "sun"],

  ["tail",                 "twoCarsClose"],
  ["follow",               "twoCarsGap"],
  ["distance",             "twoCarsGap"],
  ["two-second",           "twoCarsGap"],
  ["four-second",          "twoCarsGap"],
  ["stopping",             "twoCarsGap"],
  ["gap",                  "twoCarsGap"],

  ["overtak",              "overtake"],
  ["lane",                 "lanes"],
  ["dual",                 "lanes"],

  ["children",             "schoolKids"],
  ["school",               "schoolKids"],

  ["dashboard",            "dashboardWarn"],
  ["warning light",        "dashboardWarn"],
  ["red light",            "dashboardWarn"],

  ["mock",                 "abstractDawn"],
  ["recognition",          "abstractDawn"],
  ["integration",          "abstractDawn"],
  ["practice",             "abstractDawn"],
  ["tired",                "abstractDawn"],
  ["mood",                 "abstractDawn"],
  ["other driver",         "twoCarsGap"],
];

function deriveKey(text = "") {
  const haystack = text.toLowerCase();
  for (const [needle, key] of KEY_MATCHERS) {
    if (haystack.includes(needle)) return key;
  }
  return "abstractDawn";
}

// ─── Generic backplate ────────────────────────────────────────────
const Backplate = ({ tint = PALETTE.skySoft, accent = PALETTE.sky }) => (
  <>
    <defs>
      <linearGradient id="bp" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={tint} />
        <stop offset="1" stopColor={accent} />
      </linearGradient>
    </defs>
    <rect width="320" height="180" rx="20" fill="url(#bp)" />
  </>
);

// ─── Individual illustrations ────────────────────────────────────
// Each is a 320×180 SVG snippet (without the outer <svg> tag — added by parent).

const ILL = {
  abstractDawn: () => (
    <>
      <Backplate />
      <circle cx="160" cy="120" r="46" fill={PALETTE.amber} opacity="0.8" />
      <path d="M40 130 Q160 90 280 130 L280 180 L40 180 Z" fill={PALETTE.cream} opacity="0.7" />
      <path d="M40 142 Q160 110 280 142" stroke={PALETTE.gold} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5" />
    </>
  ),

  // ─── Lines ───────────────────────────────────────
  lineSolidWhite: () => (
    <>
      <Backplate />
      <rect x="0" y="60" width="320" height="80" fill={PALETTE.ink} opacity="0.78" rx="0" />
      <rect x="155" y="60" width="10" height="80" fill={PALETTE.cream} />
      <rect x="40" y="90" width="60" height="20" rx="3" fill={PALETTE.cream} opacity="0.85" />
      <rect x="220" y="90" width="60" height="20" rx="3" fill={PALETTE.amber} opacity="0.7" />
    </>
  ),
  lineDoubleWhite: () => (
    <>
      <Backplate />
      <rect x="0" y="60" width="320" height="80" fill={PALETTE.ink} opacity="0.78" />
      <rect x="148" y="60" width="6" height="80" fill={PALETTE.cream} />
      <rect x="166" y="60" width="6" height="80" fill={PALETTE.cream} />
    </>
  ),
  lineCentre: () => ILL.lineSolidWhite(),
  lineDashed: () => (
    <>
      <Backplate />
      <rect x="0" y="60" width="320" height="80" fill={PALETTE.ink} opacity="0.78" />
      {[20, 70, 120, 170, 220, 270].map((x) => (
        <rect key={x} x={x} y="92" width="32" height="10" rx="2" fill={PALETTE.cream} />
      ))}
    </>
  ),

  // ─── Junctions ───────────────────────────────────
  roundabout: () => (
    <>
      <Backplate />
      <rect x="0" y="80" width="320" height="40" fill={PALETTE.ink} opacity="0.78" />
      <rect x="140" y="0" width="40" height="180" fill={PALETTE.ink} opacity="0.78" />
      <circle cx="160" cy="100" r="40" fill={PALETTE.cream} />
      <circle cx="160" cy="100" r="22" fill={PALETTE.green} />
      <circle cx="160" cy="100" r="40" fill="none" stroke={PALETTE.cream} strokeDasharray="3 4" strokeWidth="1.5" />
      {/* Arrows around the roundabout */}
      <path d="M120 80 a40 40 0 0 1 25 -22" stroke={PALETTE.gold} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M138 56 l8 -2 l-2 8" stroke={PALETTE.gold} strokeWidth="2" fill="none" strokeLinecap="round" />
    </>
  ),
  junction: () => (
    <>
      <Backplate />
      <rect x="0" y="78" width="320" height="44" fill={PALETTE.ink} opacity="0.78" />
      <rect x="138" y="0" width="44" height="180" fill={PALETTE.ink} opacity="0.78" />
      <rect x="156" y="0" width="8" height="80" fill={PALETTE.cream} opacity="0.6" strokeDasharray="6 6" />
      <rect x="0" y="98" width="138" height="4" fill={PALETTE.cream} />
      <rect x="182" y="98" width="138" height="4" fill={PALETTE.cream} />
    </>
  ),

  // ─── Pedestrian / cyclist ───────────────────────
  pedestrianCrossing: () => (
    <>
      <Backplate tint={PALETTE.lavender} accent={PALETTE.skySoft} />
      <rect x="0" y="100" width="320" height="80" fill={PALETTE.inkSoft} opacity="0.18" />
      {[50, 90, 130, 170, 210, 250].map((x) => (
        <rect key={x} x={x} y="108" width="22" height="60" fill={PALETTE.cream} rx="2" />
      ))}
      {/* Walking person (geometric) */}
      <circle cx="148" cy="58" r="10" fill={PALETTE.gold} />
      <rect x="142" y="68" width="14" height="34" rx="6" fill={PALETTE.rust} />
      <rect x="138" y="100" width="6" height="22" rx="3" fill={PALETTE.ink} />
      <rect x="156" y="100" width="6" height="22" rx="3" fill={PALETTE.ink} />
      {/* Traffic light pole */}
      <rect x="246" y="46" width="28" height="44" rx="4" fill={PALETTE.ink} opacity="0.8" />
      <circle cx="260" cy="56" r="4" fill={PALETTE.red} />
      <circle cx="260" cy="68" r="4" fill={PALETTE.amber} />
      <circle cx="260" cy="80" r="4" fill={PALETTE.greenInk} />
      <rect x="258" y="90" width="4" height="50" fill={PALETTE.ink} opacity="0.7" />
    </>
  ),
  cyclist: () => (
    <>
      <Backplate />
      <rect x="0" y="120" width="320" height="60" fill={PALETTE.ink} opacity="0.78" />
      {/* Bicycle */}
      <circle cx="120" cy="130" r="22" fill="none" stroke={PALETTE.cream} strokeWidth="3" />
      <circle cx="200" cy="130" r="22" fill="none" stroke={PALETTE.cream} strokeWidth="3" />
      <path d="M120 130 L160 100 L200 130" stroke={PALETTE.cream} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M160 100 L160 80" stroke={PALETTE.cream} strokeWidth="3" strokeLinecap="round" />
      {/* Rider */}
      <circle cx="160" cy="68" r="9" fill={PALETTE.gold} />
      <path d="M160 80 L160 96" stroke={PALETTE.rust} strokeWidth="6" strokeLinecap="round" />
    </>
  ),
  horseRider: () => (
    <>
      <Backplate />
      <rect x="0" y="120" width="320" height="60" fill={PALETTE.green} />
      <ellipse cx="160" cy="118" rx="60" ry="22" fill={PALETTE.gold} />
      <rect x="118" y="118" width="6" height="34" fill={PALETTE.gold} />
      <rect x="146" y="118" width="6" height="34" fill={PALETTE.gold} />
      <rect x="174" y="118" width="6" height="34" fill={PALETTE.gold} />
      <rect x="202" y="118" width="6" height="34" fill={PALETTE.gold} />
      <circle cx="218" cy="100" r="10" fill={PALETTE.gold} />
      <circle cx="160" cy="76" r="8" fill={PALETTE.rust} />
    </>
  ),

  // ─── Cars ───────────────────────────────────────
  twoCarsGap: () => (
    <>
      <Backplate />
      <rect x="0" y="100" width="320" height="80" fill={PALETTE.ink} opacity="0.78" />
      <rect x="155" y="100" width="10" height="80" fill={PALETTE.cream} opacity="0.7" />
      {/* Car 1 (left, ahead) */}
      <rect x="40" y="116" width="74" height="34" rx="8" fill={PALETTE.rust} />
      <rect x="50" y="106" width="54" height="14" rx="6" fill={PALETTE.rust} opacity="0.85" />
      <circle cx="56" cy="154" r="7" fill={PALETTE.ink} />
      <circle cx="98" cy="154" r="7" fill={PALETTE.ink} />
      {/* Car 2 (right, following) */}
      <rect x="216" y="116" width="74" height="34" rx="8" fill={PALETTE.gold} />
      <rect x="226" y="106" width="54" height="14" rx="6" fill={PALETTE.gold} opacity="0.85" />
      <circle cx="232" cy="154" r="7" fill={PALETTE.ink} />
      <circle cx="274" cy="154" r="7" fill={PALETTE.ink} />
      {/* Soft gap arrow */}
      <path d="M122 90 L208 90" stroke={PALETTE.greenInk} strokeWidth="2" strokeLinecap="round" strokeDasharray="2 4" />
      <text x="165" y="88" textAnchor="middle" fontSize="9" fontFamily="Inter, sans-serif" fill={PALETTE.greenInk} letterSpacing="2">SPACE</text>
    </>
  ),
  twoCarsClose: () => (
    <>
      <Backplate tint={PALETTE.skySoft} accent={PALETTE.lavender} />
      <rect x="0" y="100" width="320" height="80" fill={PALETTE.ink} opacity="0.78" />
      <rect x="50" y="116" width="74" height="34" rx="8" fill={PALETTE.rust} />
      <circle cx="66" cy="154" r="7" fill={PALETTE.ink} />
      <circle cx="108" cy="154" r="7" fill={PALETTE.ink} />
      <rect x="138" y="116" width="74" height="34" rx="8" fill={PALETTE.amber} />
      <circle cx="154" cy="154" r="7" fill={PALETTE.ink} />
      <circle cx="196" cy="154" r="7" fill={PALETTE.ink} />
      <text x="170" y="80" textAnchor="middle" fontSize="11" fontFamily="Inter, sans-serif" fill={PALETTE.red} letterSpacing="3">TOO CLOSE</text>
    </>
  ),
  motorway: () => (
    <>
      <Backplate />
      <rect x="0" y="60" width="320" height="120" fill={PALETTE.ink} opacity="0.78" />
      <rect x="0" y="98" width="320" height="2" fill={PALETTE.cream} />
      <rect x="0" y="138" width="320" height="2" fill={PALETTE.cream} />
      <rect x="155" y="60" width="10" height="120" fill={PALETTE.amber} />
      {/* Cars */}
      <rect x="40" y="76" width="60" height="14" rx="4" fill={PALETTE.rust} />
      <rect x="180" y="112" width="60" height="14" rx="4" fill={PALETTE.gold} />
      <rect x="220" y="148" width="60" height="14" rx="4" fill={PALETTE.greenInk} />
    </>
  ),
  overtake: () => ILL.motorway(),
  lanes: () => ILL.motorway(),

  // ─── Signs ─────────────────────────────────────
  speedSign: () => (
    <>
      <Backplate />
      <circle cx="160" cy="90" r="48" fill={PALETTE.cream} />
      <circle cx="160" cy="90" r="48" fill="none" stroke={PALETTE.red} strokeWidth="6" />
      <text x="160" y="100" textAnchor="middle" fontSize="32" fontFamily="Inter, sans-serif" fontWeight="700" fill={PALETTE.ink}>30</text>
      <rect x="156" y="138" width="8" height="38" fill={PALETTE.inkSoft} />
    </>
  ),
  warningTriangle: () => (
    <>
      <Backplate />
      <path d="M160 38 L218 132 L102 132 Z" fill={PALETTE.cream} stroke={PALETTE.red} strokeWidth="6" strokeLinejoin="round" />
      <rect x="156" y="78" width="8" height="32" rx="2" fill={PALETTE.ink} />
      <circle cx="160" cy="120" r="4" fill={PALETTE.ink} />
      <rect x="156" y="138" width="8" height="38" fill={PALETTE.inkSoft} />
    </>
  ),
  circleSign: () => (
    <>
      <Backplate />
      <circle cx="160" cy="86" r="44" fill={PALETTE.cream} />
      <circle cx="160" cy="86" r="44" fill="none" stroke={PALETTE.red} strokeWidth="7" />
      <rect x="124" y="80" width="72" height="12" rx="2" fill={PALETTE.ink} />
      <rect x="156" y="134" width="8" height="42" fill={PALETTE.inkSoft} />
    </>
  ),
  circleMandatory: () => (
    <>
      <Backplate />
      <circle cx="160" cy="86" r="44" fill="#3D6FB8" />
      <path d="M150 70 L150 102 L138 90 M150 70 L162 70 L162 102" stroke={PALETTE.cream} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M138 86 L182 86 M170 76 L182 86 L170 96" stroke={PALETTE.cream} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="156" y="134" width="8" height="42" fill={PALETTE.inkSoft} />
    </>
  ),
  infoSign: () => (
    <>
      <Backplate />
      <rect x="100" y="50" width="120" height="76" rx="6" fill="#5BA17F" />
      <rect x="116" y="74" width="88" height="6" rx="2" fill={PALETTE.cream} />
      <rect x="116" y="90" width="60" height="6" rx="2" fill={PALETTE.cream} />
      <rect x="156" y="130" width="8" height="46" fill={PALETTE.inkSoft} />
    </>
  ),
  stopSign: () => (
    <>
      <Backplate />
      <polygon points="120,52 200,52 230,82 230,128 200,158 120,158 90,128 90,82" fill="#C53A3A" />
      <text x="160" y="116" textAnchor="middle" fontSize="22" fontFamily="Inter, sans-serif" fontWeight="800" fill={PALETTE.cream} letterSpacing="2">STOP</text>
      <rect x="156" y="158" width="8" height="22" fill={PALETTE.inkSoft} />
    </>
  ),
  giveWaySign: () => (
    <>
      <Backplate />
      <path d="M102 52 L218 52 L160 152 Z" fill={PALETTE.cream} stroke={PALETTE.red} strokeWidth="6" strokeLinejoin="round" />
      <rect x="156" y="158" width="8" height="22" fill={PALETTE.inkSoft} />
    </>
  ),
  trafficLight: () => (
    <>
      <Backplate />
      <rect x="128" y="34" width="64" height="118" rx="10" fill={PALETTE.ink} opacity="0.86" />
      <circle cx="160" cy="60" r="14" fill={PALETTE.red} />
      <circle cx="160" cy="92" r="14" fill={PALETTE.amber} />
      <circle cx="160" cy="124" r="14" fill={PALETTE.greenInk} />
      <rect x="156" y="152" width="8" height="22" fill={PALETTE.inkSoft} />
    </>
  ),

  // ─── Vehicle ───────────────────────────────────
  tyre: () => (
    <>
      <Backplate />
      <circle cx="160" cy="90" r="60" fill={PALETTE.ink} opacity="0.85" />
      <circle cx="160" cy="90" r="42" fill={PALETTE.inkSoft} opacity="0.7" />
      <circle cx="160" cy="90" r="22" fill={PALETTE.cream} />
      {/* Tread marks */}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
        const r = (deg * Math.PI) / 180;
        const x1 = 160 + Math.cos(r) * 46;
        const y1 = 90 + Math.sin(r) * 46;
        const x2 = 160 + Math.cos(r) * 58;
        const y2 = 90 + Math.sin(r) * 58;
        return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke={PALETTE.cream} strokeWidth="3" strokeLinecap="round" />;
      })}
    </>
  ),
  brakes: () => (
    <>
      <Backplate />
      <rect x="40" y="90" width="240" height="46" rx="10" fill={PALETTE.rust} />
      <rect x="50" y="80" width="220" height="14" rx="6" fill={PALETTE.rust} opacity="0.85" />
      <circle cx="64" cy="138" r="8" fill={PALETTE.ink} />
      <circle cx="256" cy="138" r="8" fill={PALETTE.ink} />
      {/* Glowing brake lights */}
      <circle cx="42" cy="112" r="9" fill={PALETTE.red} opacity="0.95" />
      <circle cx="42" cy="112" r="14" fill={PALETTE.red} opacity="0.25" />
    </>
  ),
  headlights: () => (
    <>
      <Backplate />
      <rect x="80" y="80" width="160" height="44" rx="10" fill={PALETTE.gold} />
      <rect x="92" y="70" width="136" height="16" rx="6" fill={PALETTE.gold} opacity="0.85" />
      <circle cx="100" cy="118" r="7" fill={PALETTE.ink} />
      <circle cx="220" cy="118" r="7" fill={PALETTE.ink} />
      <circle cx="74" cy="100" r="7" fill={PALETTE.amber} />
      <circle cx="74" cy="100" r="14" fill={PALETTE.amber} opacity="0.25" />
      <circle cx="246" cy="100" r="7" fill={PALETTE.amber} />
      <circle cx="246" cy="100" r="14" fill={PALETTE.amber} opacity="0.25" />
    </>
  ),
  hazardLights: () => ILL.headlights(),
  dashboardWarn: () => (
    <>
      <Backplate tint={PALETTE.lavender} accent={PALETTE.skySoft} />
      <rect x="44" y="46" width="232" height="100" rx="14" fill={PALETTE.ink} opacity="0.86" />
      <circle cx="100" cy="96" r="14" fill={PALETTE.red} />
      <circle cx="160" cy="96" r="14" fill={PALETTE.amber} />
      <circle cx="220" cy="96" r="14" fill={PALETTE.greenInk} />
      <text x="100" y="100" textAnchor="middle" fontSize="11" fontFamily="Inter, sans-serif" fill={PALETTE.cream} fontWeight="700">!</text>
    </>
  ),

  // ─── Weather ────────────────────────────────────
  rain: () => (
    <>
      <Backplate tint="#D8E2EC" accent="#C5D2DE" />
      <ellipse cx="160" cy="56" rx="64" ry="16" fill={PALETTE.inkSoft} opacity="0.55" />
      <ellipse cx="200" cy="46" rx="44" ry="12" fill={PALETTE.inkSoft} opacity="0.4" />
      {[60, 100, 140, 180, 220, 260].map((x, i) => (
        <line
          key={x}
          x1={x}
          y1={i % 2 === 0 ? 86 : 96}
          x2={x - 6}
          y2={i % 2 === 0 ? 130 : 140}
          stroke={PALETTE.sky}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      ))}
      <rect x="0" y="148" width="320" height="32" fill={PALETTE.ink} opacity="0.75" />
    </>
  ),
  ice: () => (
    <>
      <Backplate tint="#E2EAF0" accent="#CFDDE6" />
      <rect x="0" y="100" width="320" height="80" fill="#A6B6C2" />
      {[40, 100, 160, 220, 280].map((x) => (
        <path key={x} d={`M${x} 130 L${x + 5} 122 L${x + 10} 130 L${x + 5} 138 Z`} fill={PALETTE.cream} opacity="0.7" />
      ))}
      <text x="160" y="60" textAnchor="middle" fontSize="11" fontFamily="Inter, sans-serif" fill={PALETTE.inkSoft} letterSpacing="3">ICE</text>
    </>
  ),
  fog: () => (
    <>
      <Backplate tint="#E5E5E5" accent="#D2D2D2" />
      <ellipse cx="160" cy="80" rx="120" ry="16" fill={PALETTE.cream} opacity="0.65" />
      <ellipse cx="120" cy="100" rx="100" ry="14" fill={PALETTE.cream} opacity="0.55" />
      <ellipse cx="200" cy="120" rx="100" ry="14" fill={PALETTE.cream} opacity="0.45" />
      <rect x="0" y="148" width="320" height="32" fill={PALETTE.inkSoft} opacity="0.6" />
    </>
  ),
  sun: () => (
    <>
      <Backplate tint="#FFEFD5" accent="#F8DCB0" />
      <circle cx="160" cy="76" r="32" fill={PALETTE.amber} />
      <circle cx="160" cy="76" r="44" fill={PALETTE.amber} opacity="0.3" />
      <rect x="0" y="130" width="320" height="50" fill={PALETTE.ink} opacity="0.7" />
    </>
  ),

  // ─── Vulnerable users ──────────────────────────
  schoolKids: () => (
    <>
      <Backplate tint={PALETTE.skySoft} accent={PALETTE.paleSand} />
      <rect x="0" y="130" width="320" height="50" fill={PALETTE.ink} opacity="0.75" />
      {/* 2 small figures */}
      <circle cx="120" cy="86" r="9" fill={PALETTE.amber} />
      <rect x="113" y="96" width="14" height="28" rx="5" fill={PALETTE.rust} />
      <circle cx="170" cy="86" r="9" fill={PALETTE.gold} />
      <rect x="163" y="96" width="14" height="28" rx="5" fill="#3D6FB8" />
      {/* Warning sign */}
      <path d="M250 90 L286 138 L214 138 Z" fill={PALETTE.cream} stroke={PALETTE.red} strokeWidth="4" strokeLinejoin="round" />
    </>
  ),
};

/**
 * <CardIllustration />
 * @prop variant         — flash | precision | scenario  (changes backplate hue)
 * @prop imageUrl        — REAL photo URL (preferred for learning recognition).
 *                         When present, renders the photo with elegant calm
 *                         treatment.
 * @prop revealed        — when FALSE, the image is blurred + slightly
 *                         desaturated + cropped tighter so the answer cannot
 *                         be read from the picture (pure recognition learning).
 *                         When TRUE, the image becomes fully clear with a
 *                         soft 600ms transition.
 * @prop illustrationKey — explicit SVG key (overrides text-derivation, used
 *                         when no imageUrl OR as fallback)
 * @prop text            — text used to derive an SVG key automatically
 * @prop className       — appended to wrapper
 *
 * Strategy: REAL > SVG. Real photos teach recognition; SVG is the fallback
 * (or used for purely abstract days like Mock / Integration).
 *
 * Recognition pedagogy: hide first → think → reveal → confirm. The photo
 * doesn't spoil the answer until the user has committed to the question.
 */
export default function CardIllustration({
  variant = "flash",
  imageUrl,
  revealed = true,   // default true — only CardDeck overrides for question state
  illustrationKey,
  text = "",
  className = "",
}) {
  const key = useMemo(
    () => illustrationKey || deriveKey(text),
    [illustrationKey, text]
  );
  const Render = ILL[key] || ILL.abstractDawn;
  const [imgFailed, setImgFailed] = useState(false);

  // Per design system v5 — REAL photos only, never SVG concepts.
  // If the card has no specific image_url, render NO image (clean
  // text-only card). Earlier we fell back to ambient_horizon.png on
  // every imageless card, which made every Day 3+ card look identical.
  // Resolve relative API paths against REACT_APP_BACKEND_URL so the photo
  // loads cross-origin (frontend domain ≠ backend domain in this env).
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const resolvedImage = imageUrl
    ? (imageUrl.startsWith("http") ? imageUrl : `${API_BASE}${imageUrl}`)
    : null;
  const showPhoto = !!resolvedImage && !imgFailed;

  return (
    <div
      className={`relative w-full overflow-hidden rounded-[18px] ${className}`}
      style={{
        boxShadow:
          "0 6px 18px rgba(40,60,80,0.07), inset 0 1px 0 rgba(255,255,255,0.85)",
        aspectRatio: "320 / 180",
      }}
      data-testid={
        showPhoto
          ? `card-illustration-photo${revealed ? "-revealed" : "-hidden"}`
          : `card-illustration-${key}`
      }
      aria-hidden
    >
      {showPhoto ? (
        <>
          <img
            src={resolvedImage}
            alt=""
            onError={() => setImgFailed(true)}
            className="absolute inset-0 w-full h-full"
            style={{
              objectFit: "cover",
              // Pre-reveal: blur + scale so cropped edges don't show through.
              // Post-reveal: clear photo with gentle calm pull-back.
              filter: revealed
                ? "saturate(0.92) contrast(0.96) blur(0px)"
                : "saturate(0.78) contrast(0.92) blur(20px)",
              transform: revealed ? "scale(1)" : "scale(1.12)",
              transition:
                "filter 600ms cubic-bezier(0.16,1,0.3,1), transform 700ms cubic-bezier(0.16,1,0.3,1)",
              display: "block",
            }}
            loading="lazy"
            decoding="async"
          />
          {/* Pre-reveal cool wash on top — softens further, hides hint */}
          {!revealed && (
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(233,240,244,0.32) 0%, rgba(220,231,238,0.18) 100%)",
                backdropFilter: "blur(2px)",
                WebkitBackdropFilter: "blur(2px)",
                transition: "opacity 600ms ease",
              }}
            />
          )}
          {/* Soft top highlight (Apple-clarity) */}
          <div
            className="absolute inset-x-0 top-0 h-1/3 pointer-events-none"
            style={{
              background:
                "linear-gradient(to bottom, rgba(255,255,255,0.18), transparent)",
            }}
          />
          {/* Soft bottom vignette (cool-toned) */}
          <div
            className="absolute inset-x-0 bottom-0 h-1/4 pointer-events-none"
            style={{
              background:
                "linear-gradient(to top, rgba(40,60,80,0.10), transparent)",
            }}
          />
          {/* Subtle "tap to see clearly" affordance ring while hidden */}
          {!revealed && (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{ opacity: 0.42 }}
            >
              <span
                className="text-[9px] tracking-[0.42em] uppercase"
                style={{
                  color: "rgba(255,255,255,0.95)",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  textShadow: "0 1px 6px rgba(40,60,80,0.55)",
                }}
              >
                think first
              </span>
            </div>
          )}
        </>
      ) : (
        // Text-only card: clean cool-blue gradient backdrop. No SVG concepts,
        // no fallback photo (per design system v5: real photos only OR clean
        // gradient — never the same horizon photo on every card).
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(31,49,72,0.96) 0%, rgba(46,71,99,0.92) 50%, rgba(31,49,72,0.96) 100%)",
          }}
        >
          {/* Soft horizon glow centred — gives the card depth without a photo */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 70% 45% at 50% 60%, rgba(77,163,255,0.18) 0%, rgba(77,163,255,0) 70%)",
            }}
          />
          {/* "Think first" ring while hidden — same as photo path */}
          {!revealed && (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{ opacity: 0.55 }}
            >
              <span
                className="text-[9px] tracking-[0.42em] uppercase"
                style={{
                  color: "rgba(255,255,255,0.85)",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  textShadow: "0 1px 6px rgba(13,27,42,0.65)",
                }}
              >
                think first
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Export for tests
export { deriveKey, PALETTE as ILLUSTRATION_PALETTE };
